import { useCallback, useRef, useState } from 'react';
import { standardRequest } from 'shared/utils/request';
import { withLocalePrefix } from 'shared/utils/translation';
import type { ConfigurationAssistantProposal } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.configuration_assistant');

export type ConfigurationAssistantMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  // Engineered prompts (auto-draft, quick replies) are sent to the assistant
  // but should not be rendered as if the recruiter typed them.
  hidden?: boolean;
};

type SendMessageResponse = {
  chat_id: number;
  assistant_message: {
    id: number;
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
  };
  proposal?: ConfigurationAssistantProposal;
};

type SendMessageOptions = {
  includeCalibration?: boolean;
  hidden?: boolean;
  // Rendered in place of the text actually sent, e.g. a quick-reply label
  // standing in for the engineered prompt it triggers.
  displayContent?: string;
};

export const useConfigurationAssistantChat = (chatsPath: string) => {
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ConfigurationAssistantMessage[]>([]);
  const [isResponding, setIsResponding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestProposal, setLatestProposal] =
    useState<ConfigurationAssistantProposal | null>(null);

  const activeChatIdRef = useRef<number | null>(null);
  const isRespondingRef = useRef(false);

  const sendMessage = useCallback(
    async (text: string, options: SendMessageOptions = {}) => {
      if (isRespondingRef.current) {
        return;
      }

      const userMessage: ConfigurationAssistantMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: options.displayContent ?? text,
        hidden: options.hidden,
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsResponding(true);
      isRespondingRef.current = true;
      setError(null);

      try {
        const response = await standardRequest.post<SendMessageResponse>(
          chatsPath,
          {
            message: text,
            chat_id: activeChatIdRef.current,
            include_calibration: options.includeCalibration ?? true,
          }
        );

        activeChatIdRef.current = response.data.chat_id;
        setActiveChatId(response.data.chat_id);
        setMessages((currentMessages) => [
          ...currentMessages,
          {
            id: `assistant-${response.data.assistant_message.id}`,
            role: 'assistant',
            content: response.data.assistant_message.content,
          },
        ]);
        setLatestProposal(response.data.proposal ?? null);
      } catch {
        // The message never reached the server; drop the optimistic copy so
        // the visible transcript matches what the assistant will see.
        setMessages((currentMessages) =>
          currentMessages.filter((message) => message.id !== userMessage.id)
        );
        setError(t('errors.send_failed'));
      } finally {
        setIsResponding(false);
        isRespondingRef.current = false;
      }
    },
    [chatsPath]
  );

  return {
    activeChatId,
    messages,
    isResponding,
    error,
    latestProposal,
    sendMessage,
  };
};
